import { useQuery } from '@tanstack/react-query';
import Fuse from 'fuse.js';
import type {
  V3ApiSuccessfulResponse,
  V3ApiFailedResponse,
} from 'javascripts/services/v3ApiClient';
import { request } from 'javascripts/services/v3ApiClient';
import { PageTemplateObject } from 'data/v3/data-contracts';
import { useCurrentCompany } from 'javascripts/hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LD_DOCUMENTS_DOCUMENT_BUILDER } from 'constants/featureFlags';
import {
  examplePagesKeys,
  getExamplePagesOrder,
  translations,
} from 'components/projects/pages/examples/examplePages';
import { translate } from 'translations/i18n';
import {
  getRecentlyUsedPagesTemplates,
  sortPageTemplatesAlphabetically,
} from './helpers';

export type GetPageTemplatesResponse = V3ApiSuccessfulResponse<
  PageTemplateObject[]
>;

export type Filter = {
  key: string;
  label: string;
};

export const PAGE_TEMPLATES_QUERY_KEY = 'PageTemplates';

export const FILTER_KEYS = {
  ALL: 'ALL',
  EXAMPLES: 'EXAMPLES',
  YOUR_COMPANY: 'YOUR_COMPANY',
};

const options = {
  threshold: 0.3,
  ignoreLocation: true,
  keys: ['title'],
};

export const useGetPageTemplates = ({
  searchTerm,
  activeFilter,
  includeExamples = false,
}: {
  searchTerm?: string;
  activeFilter?: Filter;
  includeExamples?: boolean;
} = {}) => {
  const { currentCompany } = useCurrentCompany();

  const { data, isLoading, isError, refetch, isFetching } = useQuery<
    GetPageTemplatesResponse,
    V3ApiFailedResponse
  >({
    queryFn: () => {
      return request({
        url: `page_templates`,
        params: {
          limit: 500,
        },
      });
    },
    queryKey: [PAGE_TEMPLATES_QUERY_KEY, currentCompany.id],
  });

  const companyPageTemplates = data?.data || [];

  // Invoice Page Beta uses Document Builder flag to serve to same beta group
  const { [LD_DOCUMENTS_DOCUMENT_BUILDER]: isBetaFlagEnabled } = useFlags();

  // Include hardcoded examples
  const examples = includeExamples
    ? getExamplePagesOrder({ includePagesBetaFeatures: isBetaFlagEnabled }).map(
        (key) => {
          return {
            id: examplePagesKeys[key],
            title: translate(translations[key]),
            isExample: true,
          };
        },
      )
    : [];

  const allPageTemplates = sortPageTemplatesAlphabetically({
    pageTemplates: [...companyPageTemplates, ...examples],
    defaultTitleComparator: translate('common.untitled'),
  });

  const recentTemplates = getRecentlyUsedPagesTemplates(companyPageTemplates);

  type PageTemplateType =
    | PageTemplateObject
    | { id: string; title: string; isExample: boolean };

  const getSearchedTemplates = (
    templates: PageTemplateType[],
    searchQuery: string,
  ) => {
    if (searchQuery) {
      const fuse = new Fuse(templates, options);
      return fuse.search(searchQuery).map((result) => result.item);
    }

    return templates;
  };

  const getPageTemplates = () => {
    switch (activeFilter?.key) {
      case FILTER_KEYS.ALL:
        return getSearchedTemplates(allPageTemplates, searchTerm);
      case FILTER_KEYS.EXAMPLES:
        return getSearchedTemplates(examples, searchTerm);
      case FILTER_KEYS.YOUR_COMPANY:
        return getSearchedTemplates(companyPageTemplates, searchTerm);
      default:
        return getSearchedTemplates(allPageTemplates, searchTerm);
    }
  };

  return {
    recentTemplates:
      activeFilter?.key === FILTER_KEYS.EXAMPLES
        ? []
        : getSearchedTemplates(recentTemplates, searchTerm),
    pageTemplates: getPageTemplates(),
    refetch,
    isFetching,
    isLoading,
    isError,
  };
};
